import { assertNever } from '../assert.js';
/**
 * A run: one invocation of the installer in a customer's account.
 *
 * Recorded here rather than inferred from the cloud, because the question "what
 * did we do in this account, and how did it end" has to be answerable after the
 * task is gone, after the control plane restarts, and by someone who was not
 * there. The prototype answered it by listing live resources, which describes
 * the present and forgets the past.
 */
/**
 * What a run may do.
 *
 * The same four the dispatcher's allow list carries, and deliberately no
 * `deploy`, `adopt` or `shell` — the installer offers them, and none of them is
 * something this control plane should be able to start remotely.
 */
export const RUN_COMMANDS = ['plan', 'apply', 'output', 'destroy'];
export const isRunCommand = (value) => RUN_COMMANDS.includes(value);
/**
 * Whether a command may start an instance that does not exist yet.
 *
 * Only a plan or an apply can: those are what a new deployment is made of, so
 * a run naming no id gets one minted. A `destroy` or an `output` against a
 * minted id is always wrong and never loud — the runner finds an empty state
 * file, a destroy of nothing succeeds, and the real instance keeps running and
 * billing. That is what happened to `isno0toh` twice on 2026-09-24, so those two
 * must name an instance this control plane already knows.
 *
 * A total switch rather than a list, so a fifth command has to answer.
 */
export const mayCreateInstance = (command) => {
    switch (command) {
        case 'plan':
        case 'apply':
            return true;
        case 'output':
        case 'destroy':
            return false;
        default:
            return assertNever(command, 'run command');
    }
};
/**
 * Where a run has got to.
 *
 * `requested` is distinct from `running`: between them sits a cross-account
 * call that can fail, and a run that never started is a different fact from one
 * that started and failed. Conflating them would report a refused grant as a
 * failed deployment.
 */
export const RUN_STATES = ['requested', 'running', 'succeeded', 'failed'];
export const TERMINAL_RUN_STATES = ['succeeded', 'failed'];
export const isTerminal = (state) => TERMINAL_RUN_STATES.includes(state);
/**
 * A new run identifier.
 *
 * Time-ordered so a range query over one tenant's runs reads newest-first
 * without a secondary index, and short because it becomes the STS session name
 * inside the runner (`qim-<runId>`), which STS caps at 64 characters.
 *
 * The random suffix is not for secrecy — run ids appear in logs and URLs — but
 * so two runs started in the same millisecond cannot collide on a key.
 */
export const newRunId = (now = Date.now, random = () => Math.random().toString(36).slice(2, 8)) => `r${now().toString(36)}-${random()}`;
/**
 * A new instance key, for a run that named no deployment id (IM-139).
 *
 * The installer does not require one — `variable "deployment_id"` defaults to
 * `""` and `locals.tf` generates its own from `random_string.id`, keeping it in
 * Terraform state and recovering it on later runs. What it *does* require is
 * `account_config.key`, which has no default, so something has to name the
 * state file. That something is this.
 *
 * **This id is then the instance's id everywhere**: the state key, this control
 * plane's records, the resource names the installer builds, and the `qrvey-id`
 * tag per-instance cost reads. It is sent to the installer as `deployment_id`
 * rather than letting it generate its own.
 *
 * It was withheld at first, on the reasoning that the installer generates one
 * anyway and overriding that would be auto-filling the field under another
 * name. Use showed why that is wrong (IM-147): this mints per **run**, so a
 * plan and the apply after it each got a different key and the console grew a
 * second instance for every action taken. One identifier, decided once, is what
 * makes the next action land on the same instance.
 *
 * Eight lowercase alphanumerics: it lands in an S3 key, a URL, a sort key and —
 * since it reaches the installer — an OpenSearch domain and an Athena database,
 * so it stays inside the narrowest of those alphabets rather than the widest.
 * `deploymentIdProblem` is the same rule applied to an id somebody typed, and a
 * test holds a minted one to it.
 * There is no secrecy here — the random part only stops two instances created
 * in the same millisecond colliding on a key, which would point both at one
 * state file.
 */
export const newInstanceKey = (random = () => Math.random().toString(36).slice(2)) => `i${random()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 7)
    .padEnd(7, '0')}`;
/** Rejects anything the dispatcher's own validation would refuse. */
export const RUN_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
//# sourceMappingURL=run.js.map